import React, { ReactElement } from 'react';

import cx from 'classnames';
import flattenChildren from 'react-keyed-flatten-children';

import { AlignX, AlignY, useFlexAlign } from '~shared/hooks/useFlexAlign';
import { ReactNodeNoStrings } from '~shared/types/ReactNodeNoStrings';
import warning from '~shared/utils/warning';

import { Box } from '../Box';

const validInlineElements = ['div', 'ol', 'ul'] as const;
const spaceClassesMap = {
  '2': {
    item: 'mt-2 ml-2',
    row: '-ml-2',
    container: 'before:-mt-2',
  },
  '4': {
    item: 'mt-4 ml-4',
    row: '-ml-4',
    container: 'before:-mt-4',
  },
};

type InlineChild = 'div' | 'li';

export interface InlineProps {
  children: ReactNodeNoStrings;
  /** The parent element to render. Children will also be wrapped appropriately. */
  tagName?: typeof validInlineElements[number];
  /** The space between each item. Maps to a value on the Tailwind space scale. */
  space?: 'none' | keyof typeof spaceClassesMap;
  align?: AlignX;
  alignY?: AlignY;
}

/**
 * Renders a set of components in a row with equal spacing around
 * them, wrapping onto multiple lines when necessary.
 */
export function Inline({
  children,
  tagName = 'div',
  space = '4',
  align,
  alignY,
}: InlineProps): ReactElement {
  warning(
    validInlineElements.includes(tagName),
    `Invalid Inline tagName: '${tagName}'. Should be one of [${validInlineElements
      .map((c) => `'${c}'`)
      .join(', ')}]`
  );

  const inlineItems = flattenChildren(children);
  const isList = tagName === 'ol' || tagName === 'ul';
  const inlineItemElement: InlineChild = isList ? 'li' : 'div';

  let itemClass = '';
  let rowSpaceClass = '';
  let containerClass = '';

  if (space !== 'none') {
    itemClass = spaceClassesMap[space].item;
    rowSpaceClass = spaceClassesMap[space].row;
    containerClass = `before:block before:empty-content ${spaceClassesMap[space].container}`;
  }

  const { justifyClass, itemsClass } = useFlexAlign({ align, alignY });
  const rowClass = cx(
    'flex flex-wrap',
    justifyClass,
    itemsClass,
    rowSpaceClass
  );

  return (
    <Box className={containerClass}>
      <Box tagName={tagName} className={rowClass}>
        {React.Children.map(inlineItems, (child) =>
          child !== null && child !== undefined ? (
            <Box tagName={inlineItemElement} className={itemClass}>
              {child}
            </Box>
          ) : null
        )}
      </Box>
    </Box>
  );
}
